import { Alert } from "antd";
import { Button } from "antd-v5";
import SingleCollapsePanel from "components/SingleCollapsePanel";
import React from "react";
import {
  FieldTypes,
  generateFormFields,
  nameRegexValidator,
  urlRegexValidator,
} from "utils/formUtils";
import { duplicateNameValidator } from "utils/validatorUtil";

import {
  AUTHORIZE_TYPE,
  JiraLoginConfigType,
  USER_CONFIG_TYPE,
} from "./JiraIntegrationConstants";

export const getNameAndUrlFields = ({
  loginConfigList,
}: {
  loginConfigList: JiraLoginConfigType[];
}) => [
  {
    name: "name",
    label: "Name",
    colSpan: 12,
    rules: [
      {
        required: true,
        message: "Name is required",
      },
      duplicateNameValidator({ list: loginConfigList }),
      nameRegexValidator,
    ],
  },
  {
    name: "url",
    label: "URL",
    colSpan: 24,
    required: true,
    rules: [urlRegexValidator()],
  },
];

const versionField = {
  name: "version",
  label: "API Version",
  colSpan: 12,
  disabled: true,
};

export const advancedConfigurationField = {
  name: "advancedConfiguration",
  render: () => {
    return <AdvancedConfigSection />;
  },
};

export const getCloudTabFields = ({
  isRequired = true,
}: {
  isRequired: boolean;
}) => {
  return [
    {
      name: "email",
      label: "Email",
      colSpan: 12,
      required: true,
      rules: [
        {
          type: "email",
          message: "Invalid email",
        },
      ],
    },
    {
      name: "apiToken",
      label: "API Token",
      type: FieldTypes.PASSWORD,
      colSpan: 12,
      required: isRequired,
    },
    versionField,
  ];
};

export const getBasicAuthField = () => {
  return [
    {
      name: "userName",
      label: "User Name",
      placeholder: "User Name",
      colSpan: 12,
      required: true,
    },
    {
      name: "apiToken",
      label: "Password",
      type: FieldTypes.PASSWORD,
      colSpan: 12,
      required: true,
    },
    versionField,
  ];
};

export const getOAuthFields = ({
  getVerificationCode,
}: {
  getVerificationCode: () => void;
}) => {
  return [
    {
      name: "consumerKey",
      label: "Consumer key",
      colSpan: 12,
      required: true,
    },
    {
      name: "privateKey",
      label: "Private Key",
      type: FieldTypes.PASSWORD,
      colSpan: 12,
      required: true,
    },
    {
      type: "Custom",
      render: () => (
        <Button
          className="pull-right"
          onClick={getVerificationCode}
          type="primary"
        >
          Allow Access
        </Button>
      ),
      colSpan: 24,
    },
    versionField,
    {
      name: "verificationCode",
      label: "Verification Code",
      type: FieldTypes.PASSWORD,
      colSpan: 12,
      required: true,
    },
  ];
};

export const getPersonalAccessTokenFields = () => {
  return [
    {
      name: "userName",
      label: "User Name",
      placeholder: "User Name",
      colSpan: 12,
      required: true,
    },
    {
      name: "apiToken",
      label: "Personal Access Token",
      type: FieldTypes.PASSWORD,
      colSpan: 12,
      required: true,
    },
    versionField,
  ];
};

const configHostUrlField = [
  {
    name: "hostUrl",
    colSpan: 24,
    label: "Customize JIRA Host URL",
    rules: [urlRegexValidator()],
    help: "When using a proxy to connect to JIRA, this field can be used to update the ticket links on ArmorCode to the actual internal JIRA host. Ex: when ArmorCode connects to your on-premise JIRA via proxy URL https://ext-jira.company.com(configured above), which in turn proxies the request to https://int-jira.company.com(configured here). ArmorCode will open linked tickets on https://int-jira.company.com from its UI",
  },
];

export function AdvancedConfigSection() {
  return (
    <>
      <SingleCollapsePanel
        header="Advanced Configuration"
        className="m-b-xs"
        defaultOpen={false}
      >
        <Alert
          message={null}
          description={generateFormFields(configHostUrlField)}
          type="info"
          style={{ margin: -16 }}
        />
      </SingleCollapsePanel>
    </>
  );
}

export const getAuthTypeFieldMap = ({ ...props }) => {
  return {
    [USER_CONFIG_TYPE.REGULAR]: getCloudTabFields({
      isRequired: props.isRequired,
    }),
    [USER_CONFIG_TYPE.SERVICE_ACCOUNT]: getCloudTabFields({
      isRequired: props.isRequired,
    }),
    [AUTHORIZE_TYPE.BASIC]: getBasicAuthField(),
    [AUTHORIZE_TYPE.OAUTH]: getOAuthFields({
      getVerificationCode: props.getVerificationCode,
    }),
    [AUTHORIZE_TYPE.PERSONAL_ACCESS_TOKEN]: getPersonalAccessTokenFields(),
  };
};
